import { useCallback, useEffect, useRef } from 'react';

import { QuickstartThankYouStage } from './QuickstartThankYouStage';
import { QuickstartTheme, getResolvedCtaUrl } from '../types/theme';
import { getOutboundCtaUrl } from '../utils/steamUrls';

interface QuickstartThankYouRedirectProps {
  theme: QuickstartTheme;
  urlSlug: string;
  hasSlugConfig: boolean;
  autoRedirect?: boolean;
  onRedirect?: (steamUrl: string) => void;
}

/**
 * QuickstartThankYouRedirect Component
 *
 * Displays the thank-you / join-failure screen in the Icon Drop UI shell:
 * full-bleed stage, brand cluster, bottom copy block with the gold CTA.
 * The store button image and centred QuickstartCard layout are retired.
 *
 * CTA behaviour follows this branch's WebView contract: the resolved outbound
 * URL is handed to the shell as a real anchor href (embedded WebViews block
 * window.open), with the analytics callback firing on click before navigation.
 * Auto-redirect still uses location.href after 500ms.
 */
export const QuickstartThankYouRedirect = ({ theme, urlSlug, hasSlugConfig, autoRedirect, onRedirect }: QuickstartThankYouRedirectProps) => {
  // Resolved CTA destination as a real link so embedded WebViews can navigate.
  const ctaHref = (() => {
    const ctaUrl = getResolvedCtaUrl(theme);
    return ctaUrl ? getOutboundCtaUrl(ctaUrl, hasSlugConfig) : undefined;
  })();

  const handleCheckItOut = useCallback(() => {
    // Analytics first - the browser navigation below is what embedded WebViews
    // (AppLovin/LoopMe, etc.) honor; window.open is blocked inside them.
    if (ctaHref) {
      onRedirect?.(ctaHref);
    }
  }, [ctaHref, onRedirect]);

  const hasAutoRedirected = useRef(false);

  useEffect(() => {
    if (!autoRedirect || hasAutoRedirected.current) {
      return;
    }

    const ctaUrl = getResolvedCtaUrl(theme);
    if (!ctaUrl) {
      return;
    }

    hasAutoRedirected.current = true;

    // Auto-redirect: Use location.href instead of window.open to avoid popup blockers
    // Safari, Opera, and other browsers block window.open() when not triggered by direct user action
    const finalUrl = getOutboundCtaUrl(ctaUrl, hasSlugConfig);

    // Send analytics callback before redirect
    if (onRedirect) {
      onRedirect(finalUrl);
    }

    // Delay to ensure analytics can be sent (500ms is conservative for network latency)
    // Analytics uses fetch with keepalive:true, which helps during redirects
    setTimeout(() => {
      window.location.href = finalUrl;
    }, 500);
  }, [autoRedirect, onRedirect, theme, hasSlugConfig]);

  return (
    <QuickstartThankYouStage
      theme={theme}
      ctaHref={ctaHref}
      onCta={ctaHref ? handleCheckItOut : undefined}
      isMobile={false}
    />
  );
};
